import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FilePdf, ArrowUUpLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { Money, ConfirmDialog, StatusChip } from "../components/shared";
import { RecordNav } from "../components/RecordNav";
import { EmailReceiptDialog } from "../components/EmailReceiptDialog";
import { usePerms } from "../lib/perms";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";

const CONFIGS = {
  shipment: {
    title: "Posted Sales Shipment", source: "posted-shipments", basePath: "/sales/posted-shipment",
    back: "/sales?tab=posted&sub=ship", numKey: "shipment_no", party: "Customer", partyKey: "customer_name",
    partyPath: (d) => `/sales/customer/${d.customer_id}`, srcList: "sales-orders", srcPath: "/sales/doc",
    srcRefs: (d) => [["Invoice", d.invoice_no], ["Order", d.order_no || d.source_order_no]],
    pdf: (d) => `${API}/posted-shipments/${d.id}/packing-slip`, undoCap: "sales.post",
    canUndo: (d) => !!(d.order_no || d.source_order_no), undoUrl: (d) => `/posted-shipments/${d.id}/undo`,
    invalidate: ["posted-shipments", "sales-orders", "items"], undoTitle: "Undo this shipment?",
    undoText: (d) => `${d.shipment_no} will be reversed: the shipped quantities return to stock and its sales order reopens. This is blocked if the quantities have already been invoiced.`,
    undoDone: "Shipment undone — inventory restored", undoLabel: "Undo Shipment",
  },
  returnReceipt: {
    title: "Posted Return Receipt", source: "posted-return-receipts", basePath: "/sales/posted-return-receipt",
    back: "/sales?tab=posted&sub=rr", numKey: "receipt_no", party: "Customer", partyKey: "customer_name",
    partyPath: (d) => `/sales/customer/${d.customer_id}`, srcList: "sales-orders", srcPath: "/sales/doc",
    srcRefs: (d) => [["Return Order", d.order_no]], showTotal: "Credited",
  },
  receipt: {
    title: "Posted Purchase Receipt", source: "posted-receipts", basePath: "/purchasing/posted-receipt",
    back: "/purchasing?tab=posted&sub=receipts", numKey: "receipt_no", party: "Vendor", partyKey: "vendor_name",
    partyPath: (d) => `/purchasing/vendor/${d.vendor_id}`, srcList: "purchase-orders", srcPath: "/purchasing/doc",
    srcRefs: (d) => [["Order", d.order_no], ["Sales Order", d.sales_order_no]], showTotal: "Total", undoCap: "purchasing.post",
    pdf: (d) => `${API}/posted-receipts/${d.id}/pdf`, pdfLabel: "Print Receipt", receivedBy: true, email: true,
    canUndo: (d) => !!d.order_no, undoUrl: (d) => `/posted-receipts/${d.id}/undo`,
    invalidate: ["posted-receipts", "purchase-orders", "items"], undoTitle: "Undo this receipt?",
    undoText: (d) => `${d.receipt_no} will be reversed: the received quantities are removed from stock and its purchase order reopens. This is blocked if the order has already been invoiced.`,
    undoDone: "Receipt undone — inventory reversed", undoLabel: "Undo Receipt",
  },
};

const Field = ({ label, children, testid }) => (
  <div>
    <div className="text-xs text-muted-foreground mb-0.5">{label}</div>
    <div className="text-sm font-medium" data-testid={testid}>{children || "—"}</div>
  </div>
);

const PostedLines = ({ lines, items, location }) => {
  const sku = (id) => items.find((i) => i.id === id)?.sku || "—";
  return (
    <div className="border border-border rounded-sm overflow-x-auto">
      <Table data-testid="posted-doc-lines">
        <TableHeader><TableRow>
          <TableHead>Item No.</TableHead><TableHead>Description</TableHead><TableHead>Location</TableHead>
          <TableHead className="text-right">Quantity</TableHead><TableHead className="text-right">Unit Price</TableHead><TableHead className="text-right">Amount</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {lines.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No lines.</TableCell></TableRow>}
          {lines.map((l, i) => (
            <TableRow key={l.id || i} data-testid="posted-doc-line">
              <TableCell className="font-mono text-xs">{sku(l.item_id)}</TableCell>
              <TableCell className="text-sm">{l.description}</TableCell>
              <TableCell className="font-mono text-xs">{l.location_code || location || "—"}</TableCell>
              <TableCell className="text-right tabular text-sm">{l.quantity}</TableCell>
              <TableCell className="text-right"><Money value={l.unit_price} className="text-sm" /></TableCell>
              <TableCell className="text-right"><Money value={l.amount} className="text-sm font-medium" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const useUndo = (cfg, onDone) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (d) => (await api.post(cfg.undoUrl(d))).data,
    onSuccess: () => { cfg.invalidate.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast.success(cfg.undoDone); onDone(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
};

export default function PostedDocCard({ kind }) {
  const cfg = CONFIGS[kind];
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePerms();
  const [confirm, setConfirm] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const { data: docs = [], isLoading } = useQuery({ queryKey: [cfg.source], queryFn: async () => (await api.get(`/${cfg.source}`)).data });
  const { data: srcDocs = [] } = useQuery({ queryKey: [cfg.srcList], queryFn: async () => (await api.get(`/${cfg.srcList}`)).data });
  const { data: items = [] } = useQuery({ queryKey: ["items"], queryFn: async () => (await api.get("/items")).data });
  const undo = useUndo(cfg, () => navigate(cfg.back));
  const doc = docs.find((d) => d.id === id);
  if (isLoading) { return <div className="py-16 text-center text-muted-foreground">Loading…</div>; }
  if (!doc) { return <div className="py-16 text-center text-muted-foreground" data-testid="posted-doc-missing">This posted document could not be found.</div>; }
  const refs = cfg.srcRefs(doc).filter(([, no]) => no);
  const srcOf = (no) => srcDocs.find((s) => s.doc_no === no);
  const location = doc.location_code || refs.map(([, no]) => srcOf(no)?.location_code).find(Boolean);
  return (
    <div className="animate-fade-up pb-10" data-testid="posted-doc-card">
      <button onClick={() => navigate(cfg.back)} className="p-1.5 -ml-1.5 mb-1 text-muted-foreground hover:text-primary" data-testid="posted-doc-back"><ArrowLeft size={18} weight="bold" /></button>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div>
          <div className="text-xs text-muted-foreground">{cfg.title}</div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight font-mono" data-testid="posted-doc-title">{doc[cfg.numKey]}</h1>
        </div>
        <StatusChip status="Posted" />
        <div className="ml-auto flex items-center gap-2">
          {cfg.pdf && <button onClick={() => window.open(cfg.pdf(doc), "_blank")} data-testid="posted-doc-print" className="h-8 px-3 inline-flex items-center gap-1.5 text-sm border border-border rounded-sm hover:bg-muted transition-colors"><FilePdf size={15} weight="bold" /> {cfg.pdfLabel || "Delivery Note"}</button>}
          {cfg.email && can("purchasing.edit") && <button onClick={() => setEmailOpen(true)} data-testid="posted-doc-email" className="h-8 px-3 inline-flex items-center gap-1.5 text-sm border border-border rounded-sm hover:bg-muted transition-colors"><EnvelopeSimple size={15} weight="bold" /> Email Receipt</button>}
          {cfg.canUndo?.(doc) && can(cfg.undoCap) && <button onClick={() => setConfirm(true)} data-testid="posted-doc-undo" className="h-8 px-3 inline-flex items-center gap-1.5 text-sm border border-border rounded-sm hover:border-destructive hover:text-destructive transition-colors"><ArrowUUpLeft size={15} weight="bold" /> {cfg.undoLabel}</button>}
          <RecordNav source={cfg.source} currentId={id} sortKey={(d) => d[cfg.numKey]} basePath={cfg.basePath} testId="posted-doc-nav" />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 border border-border rounded-sm p-5 mb-6" data-testid="posted-doc-header">
        <Field label={cfg.party} testid="posted-doc-party">
          <button onClick={() => navigate(cfg.partyPath(doc))} className="text-primary hover:underline text-left">{doc[cfg.partyKey]}</button>
        </Field>
        <Field label="Posting Date" testid="posted-doc-date"><span className="font-mono">{doc.date}</span></Field>
        <Field label="External Doc No." testid="posted-doc-external">{doc.external_doc_no}</Field>
        <Field label="Location" testid="posted-doc-location"><span className="font-mono">{location}</span></Field>
        {refs.map(([label, no]) => (
          <Field key={label} label={label} testid={`posted-doc-ref-${label.toLowerCase().replace(/ /g, "-")}`}>
            {srcOf(no) ? <button onClick={() => navigate(`${cfg.srcPath}/${srcOf(no).id}`)} className="text-primary hover:underline font-mono">{no}</button> : <span className="font-mono">{no}</span>}
          </Field>
        ))}
        {doc.ship_to_name && <Field label="Ship-to" testid="posted-doc-ship-to">{[doc.ship_to_name, doc.ship_to_address, doc.ship_to_city, doc.ship_to_state, doc.ship_to_postcode].filter(Boolean).join(", ")}</Field>}
        {cfg.receivedBy && <Field label="Received By" testid="posted-doc-received-by">{doc.received_by}</Field>}
        {cfg.showTotal && <Field label={cfg.showTotal} testid="posted-doc-total"><Money value={doc.total} /></Field>}
      </div>
      <PostedLines lines={doc.lines || []} items={items} location={location} />
      {cfg.email && <EmailReceiptDialog receipt={doc} open={emailOpen} onOpenChange={setEmailOpen} />}
      {cfg.undoUrl && (
        <ConfirmDialog open={confirm} onOpenChange={setConfirm} title={cfg.undoTitle} description={cfg.undoText(doc)}
          confirmLabel={cfg.undoLabel} testid="posted-doc-undo-confirm" onConfirm={() => { undo.mutate(doc); setConfirm(false); }} />
      )}
    </div>
  );
}
